import { useLayoutEffect, useRef, useState } from "react";

// A receipt line that is a horizontal rule and nothing else. U+2500 is what
// escpos.preview_text decodes the thermal 0xC4 rule to; '-' covers an ASCII
// rule. Drawn as CSS, not glyphs: box-drawing characters come from a fallback
// font whose advance does not match the monospace stack.
export const DIVIDER_RE = /^[-─]{8,}$/;

const MIN_PX = 5;
const MAX_PX = 14;

/**
 * A printer slip (bill, check items) exactly as the server decoded it from the
 * printer bytes, sized so its widest line fills the width it is given.
 *
 * Two passes (owner, 2026-10-25: "the bill preview is going off screen"): a
 * canvas measurement picks the size, then the painted lines are checked and the
 * size shrinks until none is wider than the box. The second pass is what holds
 * on a phone with large system text, where the web view scales the font past
 * what the measurement assumed.
 */
export default function ReceiptText({ text, testId = "receipt-text" }) {
  const boxRef = useRef(null);
  const [fontPx, setFontPx] = useState(12);
  const lines = String(text || "").split("\n");

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const fit = () => {
      const width = box.clientWidth;
      if (!width) return;
      const textLines = lines.filter(l => !DIVIDER_RE.test(l));
      const ctx = document.createElement("canvas").getContext?.("2d");
      let emWidest = 0;
      if (ctx) {
        ctx.font = `100px ${getComputedStyle(box).fontFamily}`;
        for (const l of textLines) emWidest = Math.max(emWidest, ctx.measureText(l).width / 100);
      }
      if (!emWidest) emWidest = Math.max(32, ...textLines.map(l => l.length)) * 0.602;
      setFontPx(Math.max(MIN_PX, Math.min(MAX_PX, Math.floor((width / emWidest) * 10) / 10)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // Second pass: what actually painted. Any line wider than the box shrinks the
  // font in proportion; this runs again until everything fits.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || !box.clientWidth) return;
    const widest = Math.max(0, ...[...box.children].map(c => c.scrollWidth));
    if (widest > box.clientWidth + 0.5 && fontPx > MIN_PX) {
      setFontPx(f => Math.max(MIN_PX, Math.floor(f * (box.clientWidth / widest) * 10) / 10 - 0.1));
    }
  }, [fontPx, text]);

  return (
    <div
      ref={boxRef}
      className="font-mono leading-relaxed text-foreground overflow-hidden"
      style={{ fontSize: `${fontPx}px` }}
      data-testid={testId}
    >
      {lines.map((line, i) =>
        DIVIDER_RE.test(line) ? (
          <div key={i} aria-hidden="true" className="flex items-center" style={{ height: "1.625em" }}>
            <div className="w-full border-t border-current" />
          </div>
        ) : (
          <div key={i} className="whitespace-pre">{line || " "}</div>
        ),
      )}
    </div>
  );
}
